import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from '@/app/hooks';
import { useToast } from '@/components/ui/toast-store';
import { clearSentryUser } from '@/lib/sentry';
import { baseApi } from '@/services/baseApi';
import { useAuthControllerLogoutMutation } from '@/services/generated/api';
import { sessionCleared } from './authSlice';

export interface UseLogoutResult {
  logout: () => void;
  /** For disabling the trigger while the call is in flight. */
  loggingOut: boolean;
}

/**
 * Logging out, in full: the `POST /auth/logout` that erases the cookie, plus the
 * two things the browser keeps remembering after that response.
 *
 * **RTK Query's cache is emptied, and that is the part that matters.** The
 * cookie is `HttpOnly` and the SPA never sees it, so without the reset the
 * session ends on the server and the screen keeps showing the companies, the
 * folios and the DTEs of whoever has just left until something makes it refetch.
 * On a shared computer — the case that makes this button exist — that is exactly
 * what cannot happen. `sessionCleared()` does the same for the client state: the
 * active organisation and company.
 *
 * **If the call fails, NOTHING is cleared.** It is tempting to erase the local
 * state anyway and send the user to the login: it looks tidy and it is a lie in
 * the dangerous direction, because the cookie is still alive and the next visit
 * to `/` gets in without asking. A toast reports it and the session stays open,
 * which is the truth.
 *
 * After the reset a `GET /api/account` goes out, and it is not redundant: it is
 * the one `LoginPage` makes on mount anyway, now with the cookie already erased,
 * that is, the 401 confirming the session really ended.
 *
 * `clearSentryUser()` closes the third copy, the one not in the store:
 * `AppLayout` puts email, organisation and company on everything Sentry reports,
 * and only clears them in its `isError` effect. That effect does NOT run on
 * logging out — the layout unmounts and nobody asks for the account again — so
 * without this line the errors of the NEXT person on the same computer would
 * reach Sentry carrying the previous person's email.
 */
export function useLogout(): UseLogoutResult {
  const [requestLogout, { isLoading }] = useAuthControllerLogoutMutation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { toast } = useToast();

  const logout = React.useCallback(() => {
    void (async () => {
      try {
        await requestLogout().unwrap();
      } catch {
        toast({
          tone: 'danger',
          title: 'No se pudo cerrar la sesión',
          description: 'Revisa tu conexión e intenta de nuevo.',
        });
        return;
      }
      clearSentryUser();
      // The two dispatches go BEFORE the navigation, and the order is not
      // cosmetic: `navigate` can commit the login route in the same React batch
      // it is called in, so with the dispatches after it there is a window in
      // which `/login` is on screen while the store still holds the previous
      // person's organisation and company. Nothing renders from it there, which
      // is why this survived — but it is one `useSelector` away from being a
      // real leak on a shared computer, and it made
      // `AppLayout.test.tsx`'s logout case fail under CI load while passing
      // locally (run 229 on `main`).
      dispatch(sessionCleared());
      dispatch(baseApi.util.resetApiState());
      navigate('/login', { replace: true });
    })();
  }, [requestLogout, navigate, dispatch, toast]);

  return { logout, loggingOut: isLoading };
}
